import { AutoTrackConfig } from "../core/types";
import { PrivacyManager } from "../privacy/masking";

export interface AutoTrackCallbacks {
  onPageView: (properties?: Record<string, any>) => void;
  onPageExit: (properties?: Record<string, any>) => void;
  onClick: (properties: Record<string, any>) => void;
  onScroll: (depthPercent: number) => void;
  onFormStart: (formName: string, formId?: string) => void;
  onFormSubmit: (formName: string, formId?: string) => void;
  onDownload: (url: string, fileName?: string) => void;
  onOutboundLink: (url: string, target?: string) => void;
  onVideoStart: (videoSrc: string, title?: string) => void;
  onVideoComplete: (videoSrc: string, title?: string) => void;
}

export class AutoTracker {
  private config: AutoTrackConfig;
  private privacy: PrivacyManager;
  private callbacks: AutoTrackCallbacks;
  private scrollMilestones: Set<number> = new Set();
  private formsStarted: Set<HTMLFormElement> = new Set();
  private lastUrl: string = "";

  constructor(
    config: AutoTrackConfig = {},
    privacy: PrivacyManager,
    callbacks: AutoTrackCallbacks,
  ) {
    this.config = {
      pageView: true,
      pageExit: true,
      click: true,
      scroll: false,
      form: true,
      download: true,
      outboundLink: true,
      video: true,
      ...config,
    };
    this.privacy = privacy;
    this.callbacks = callbacks;
  }

  public start(): void {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      typeof window.addEventListener !== "function" ||
      typeof document.addEventListener !== "function"
    ) {
      return;
    }

    this.lastUrl = window.location.href;

    // 1. Page Views & SPA navigation
    if (this.config.pageView) {
      this.callbacks.onPageView();
      this.setupSpaListeners();
    }

    // 2. Page Exit
    if (this.config.pageExit) {
      this.setupPageExitListeners();
    }

    // 3. Clicks, Downloads & Outbound Links
    if (this.config.click || this.config.download || this.config.outboundLink) {
      this.setupClickListener();
    }

    // 4. Scroll Depth
    if (this.config.scroll) {
      this.setupScrollListener();
    }

    // 5. Form Interactions
    if (this.config.form) {
      this.setupFormListeners();
    }

    // 6. Video Tracking
    if (this.config.video) {
      this.setupVideoListeners();
    }
  }

  private setupSpaListeners(): void {
    const handleUrlChange = () => {
      const currentUrl = window.location.href;
      if (currentUrl !== this.lastUrl) {
        this.lastUrl = currentUrl;
        this.scrollMilestones.clear();
        this.callbacks.onPageView();
      }
    };

    window.addEventListener("popstate", handleUrlChange);

    // Patch pushState
    const origPushState = history.pushState;
    if (origPushState) {
      history.pushState = function (...args) {
        origPushState.apply(this, args);
        handleUrlChange();
      };
    }

    // Patch replaceState
    const origReplaceState = history.replaceState;
    if (origReplaceState) {
      history.replaceState = function (...args) {
        origReplaceState.apply(this, args);
        handleUrlChange();
      };
    }
  }

  private setupPageExitListeners(): void {
    window.addEventListener("pagehide", () => {
      this.callbacks.onPageExit();
    });
  }

  private setupClickListener(): void {
    document.addEventListener(
      "click",
      (event: MouseEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target || this.privacy.isElementIgnored(target)) return;

        // Find closest interactive element
        const interactiveEl = target.closest("a, button, input[type='button'], input[type='submit'], [role='button'], [data-thrico-track]") as HTMLElement | null;
        if (!interactiveEl || this.privacy.isElementIgnored(interactiveEl)) return;

        const isMasked = this.privacy.isElementMasked(interactiveEl);

        // Check if anchor link
        if (interactiveEl instanceof HTMLAnchorElement && interactiveEl.href) {
          const href = interactiveEl.href;
          const host = interactiveEl.hostname;
          const currentHost = window.location.hostname;

          // Check for download link
          if (this.config.download && /\.(pdf|zip|docx?|xlsx?|pptx?|csv|tar\.gz)$/i.test(href)) {
            const fileName = href.substring(href.lastIndexOf("/") + 1);
            this.callbacks.onDownload(href, fileName);
            return;
          }

          // Check for outbound link
          if (this.config.outboundLink && host && host !== currentHost) {
            this.callbacks.onOutboundLink(href, interactiveEl.target);
            return;
          }
        }

        // Generic Click
        if (this.config.click) {
          const rawText = interactiveEl.innerText || interactiveEl.getAttribute("aria-label") || (interactiveEl as HTMLInputElement).value || "";
          const text = this.privacy.sanitizeText(rawText, isMasked);
          const tag = interactiveEl.tagName.toLowerCase();
          const id = interactiveEl.id ? `#${interactiveEl.id}` : "";
          const trackLabel = interactiveEl.getAttribute("data-thrico-track") || interactiveEl.getAttribute("data-track");

          this.callbacks.onClick({
            tag,
            elementId: id,
            text,
            trackLabel: trackLabel || undefined,
          });
        }
      },
      true,
    );
  }

  private setupScrollListener(): void {
    let ticking = false;

    window.addEventListener("scroll", () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const docElem = document.documentElement;
          const totalHeight = docElem.scrollHeight - docElem.clientHeight;
          if (totalHeight > 0) {
            const scrollPercent = Math.round((window.scrollY / totalHeight) * 100);
            const thresholds = [25, 50, 75, 100];

            for (const t of thresholds) {
              if (scrollPercent >= t && !this.scrollMilestones.has(t)) {
                this.scrollMilestones.add(t);
                this.callbacks.onScroll(t);
              }
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    });
  }

  private setupFormListeners(): void {
    // Form Start (first input focus inside a form)
    document.addEventListener(
      "focusin",
      (event: FocusEvent) => {
        const target = event.target as HTMLElement | null;
        if (!target) return;

        const form = target.closest("form");
        if (form && !this.formsStarted.has(form) && !this.privacy.isElementIgnored(form)) {
          this.formsStarted.add(form);
          const formName = form.name || form.id || form.getAttribute("aria-label") || "unnamed_form";
          this.callbacks.onFormStart(formName, form.id);
        }
      },
      true,
    );

    // Form Submit
    document.addEventListener(
      "submit",
      (event: SubmitEvent) => {
        const form = event.target as HTMLFormElement | null;
        if (!form || this.privacy.isElementIgnored(form)) return;

        const formName = form.name || form.id || form.getAttribute("aria-label") || "unnamed_form";
        this.callbacks.onFormSubmit(formName, form.id);
      },
      true,
    );
  }

  private setupVideoListeners(): void {
    document.addEventListener(
      "play",
      (event: Event) => {
        const target = event.target as HTMLMediaElement | null;
        if (target && target.tagName === "VIDEO" && !this.privacy.isElementIgnored(target)) {
          const src = target.currentSrc || target.src;
          const title = target.getAttribute("title") || target.getAttribute("aria-label") || undefined;
          this.callbacks.onVideoStart(src, title);
        }
      },
      true,
    );

    document.addEventListener(
      "ended",
      (event: Event) => {
        const target = event.target as HTMLMediaElement | null;
        if (target && target.tagName === "VIDEO" && !this.privacy.isElementIgnored(target)) {
          const src = target.currentSrc || target.src;
          const title = target.getAttribute("title") || target.getAttribute("aria-label") || undefined;
          this.callbacks.onVideoComplete(src, title);
        }
      },
      true,
    );
  }
}
